import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BillsDueNotification.css';

const BillsDueNotification = ({ onClose }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBillsDueThisMonth();
  }, []);

  const fetchBillsDueThisMonth = async () => {
    try {
      const response = await axios.get('/api/expenses/bills-due-this-month', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setBills(response.data.bills);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching bills due this month:', error);
      setError('Failed to fetch bills due this month');
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async (billId) => {
    try {
      const response = await axios.post('/api/expenses/mark-bill-paid', {
        expenseId: billId
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        // Remove the bill from the list
        setBills(prevBills => prevBills.filter(bill => bill.id !== billId));
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Error marking bill as paid:', error);
      setError('Failed to mark bill as paid');
    }
  };

  const getFrequencyIcon = (frequency) => {
    switch (frequency) {
      case 'monthly': return '📅';
      case 'quarterly': return '📊';
      case 'semi-annually':
      case 'semi_annually': return '📈';
      case 'yearly': return '🗓️';
      default: return '📅';
    }
  };

  const getFrequencyColor = (frequency) => {
    switch (frequency) {
      case 'monthly': return '#3b82f6';
      case 'quarterly': return '#f59e0b';
      case 'semi-annually':
      case 'semi_annually': return '#ef4444';
      case 'yearly': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getBillTypeIcon = (billType) => {
    switch (billType) {
      case 'mortgage': return '🏠';
      case 'insurance': return '🛡️';
      case 'utilities': return '⚡';
      case 'property_taxes': return '🏛️';
      case 'maintenance': return '🔧';
      case 'hoa': return '🏘️';
      default: return '💰';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyClass = (daysUntilDue) => {
    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= 3) return 'urgent';
    if (daysUntilDue <= 7) return 'soon';
    return 'normal';
  };

  if (loading) {
    return (
      <div className="bills-notification-overlay">
        <div className="bills-notification-modal">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading bills due this month...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bills-notification-overlay">
        <div className="bills-notification-modal">
          <div className="error-message">
            <h3>❌ Error</h3>
            <p>{error}</p>
            <button onClick={onClose} className="close-btn">Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (bills.length === 0) {
    return (
      <div className="bills-notification-overlay">
        <div className="bills-notification-modal">
          <div className="no-bills">
            <h3>🎉 Great News!</h3>
            <p>No bills are due this month.</p>
            <button onClick={onClose} className="close-btn">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // Separate monthly and non-monthly bills
  const monthlyBills = bills.filter(bill => bill.frequency === 'monthly');
  const nonMonthlyBills = bills.filter(bill => bill.frequency !== 'monthly');

  return (
    <div className="bills-notification-overlay">
      <div className="bills-notification-modal">
        <div className="bills-header">
          <h2>📋 Bills Due This Month</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="bills-content">
          {/* Non-Monthly Bills (Priority) */}
          {nonMonthlyBills.length > 0 && (
            <div className="bills-section priority">
              <h3>🚨 Priority Bills (Non-Monthly)</h3>
              <div className="bills-list">
                {nonMonthlyBills.map(bill => {
                  const daysUntilDue = getDaysUntilDue(bill.next_due_date);
                  return (
                    <div key={bill.id} className={`bill-item ${getUrgencyClass(daysUntilDue)}`}>
                      <div className="bill-info">
                        <div className="bill-header">
                          <span className="bill-type-icon">{getBillTypeIcon(bill.bill_type)}</span>
                          <span className="bill-description">{bill.description}</span>
                          <span className="bill-amount">${bill.amount.toFixed(2)}</span>
                        </div>
                        <div className="bill-details">
                          <span className="property-name">{bill.property_name || 'No Property'}</span>
                          <span className="frequency" style={{ color: getFrequencyColor(bill.frequency) }}>
                            {getFrequencyIcon(bill.frequency)} {bill.frequency}
                          </span>
                          <span className="due-date">
                            Due: {formatDate(bill.next_due_date)}
                            {daysUntilDue < 0 && <span className="overdue-text"> (OVERDUE)</span>}
                            {daysUntilDue >= 0 && daysUntilDue <= 7 && <span className="urgent-text"> ({daysUntilDue} days)</span>}
                          </span>
                        </div>
                      </div>
                      <div className="bill-actions">
                        <button 
                          onClick={() => markAsPaid(bill.id)}
                          className="mark-paid-btn"
                        >
                          Mark as Paid
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Monthly Bills */}
          {monthlyBills.length > 0 && (
            <div className="bills-section monthly">
              <h3>📅 Monthly Bills</h3>
              <div className="bills-list">
                {monthlyBills.map(bill => {
                  const daysUntilDue = getDaysUntilDue(bill.next_due_date);
                  return (
                    <div key={bill.id} className={`bill-item ${getUrgencyClass(daysUntilDue)}`}>
                      <div className="bill-info">
                        <div className="bill-header">
                          <span className="bill-type-icon">{getBillTypeIcon(bill.bill_type)}</span>
                          <span className="bill-description">{bill.description}</span>
                          <span className="bill-amount">${bill.amount.toFixed(2)}</span>
                        </div>
                        <div className="bill-details">
                          <span className="property-name">{bill.property_name || 'No Property'}</span>
                          <span className="frequency" style={{ color: getFrequencyColor(bill.frequency) }}>
                            {getFrequencyIcon(bill.frequency)} {bill.frequency}
                          </span>
                          <span className="due-date">
                            Due: {formatDate(bill.next_due_date)}
                            {daysUntilDue < 0 && <span className="overdue-text"> (OVERDUE)</span>}
                            {daysUntilDue >= 0 && daysUntilDue <= 7 && <span className="urgent-text"> ({daysUntilDue} days)</span>}
                          </span>
                        </div>
                      </div>
                      <div className="bill-actions">
                        <button 
                          onClick={() => markAsPaid(bill.id)}
                          className="mark-paid-btn"
                        >
                          Mark as Paid
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bills-summary">
            <div className="summary-item">
              <span className="summary-label">Total Bills Due:</span>
              <span className="summary-value">{bills.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Amount:</span>
              <span className="summary-value">${bills.reduce((sum, bill) => sum + bill.amount, 0).toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Priority Bills:</span>
              <span className="summary-value">{nonMonthlyBills.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillsDueNotification;


